import { createContext, useCallback, useContext, useEffect, useMemo, useState, type ReactNode } from 'react'
import { api, formatMoney, type CartView } from '../api/client'
import { useSession } from './session'

/**
 * The basket.
 *
 * The server owns it. This holds a copy for rendering and never computes a total: the subtotal shown here is
 * the one the server returned and is labelled an estimate, because the authoritative figure only exists once
 * the checkout has priced the basket with any coupon applied.
 *
 * Quantities are optimistic. Pressing plus updates the number immediately and then reconciles with the
 * server's answer, because a shopping basket that waits for a round trip before the number changes feels
 * broken on a slow connection, which is most of them.
 */
interface CartState {
  cart: CartView | null
  loading: boolean
  error: string | null
  itemCount: number
  subtotalLabel: string
  refresh: () => Promise<void>
  add: (variantId: string, quantity?: number) => Promise<void>
  setQuantity: (variantId: string, quantity: number) => Promise<void>
  remove: (variantId: string) => Promise<void>
  applyCoupon: (code: string) => Promise<void>
  removeCoupon: () => Promise<void>
  clearError: () => void
}

const CartContext = createContext<CartState | null>(null)

export function CartProvider({ children }: { children: ReactNode }) {
  const { signedIn } = useSession()
  const [cart, setCart] = useState<CartView | null>(null)
  const [loading, setLoading] = useState(true)
  const [error, setError] = useState<string | null>(null)

  const refresh = useCallback(async () => {
    setLoading(true)

    try {
      setCart(await api.cart.view())
      setError(null)
    } catch (failure) {
      // A basket that cannot be read is treated as an empty one rather than as an error screen. The customer
      // can still browse, and the next change to the basket will tell them if something is actually wrong.
      setCart(null)
      setError(failure instanceof Error ? failure.message : null)
    } finally {
      setLoading(false)
    }
  }, [])

  useEffect(() => {
    void refresh()
  }, [refresh, signedIn])

  /** Runs a mutation and adopts whatever basket the server returns. */
  const mutate = useCallback(async (operation: () => Promise<CartView>) => {
    setError(null)

    try {
      setCart(await operation())
    } catch (failure) {
      setError(failure instanceof Error ? failure.message : 'That change could not be made.')

      // Re-read rather than guess. The server refused for a reason, and the reason is usually that stock moved,
      // which the refreshed basket shows.
      await refresh()
    }
  }, [refresh])

  const value = useMemo<CartState>(
    () => ({
      cart,
      loading,
      error,
      itemCount: cart?.lineCount ?? 0,
      subtotalLabel: formatMoney(cart?.estimatedSubtotalPaise ?? 0),
      refresh,
      // Parameter types are written out rather than inferred. The interface above is the contract, and a
      // callback whose signature depends on inference is one refactor away from accepting anything.
      add: (variantId: string, quantity = 1) => mutate(() => api.cart.add(variantId, quantity)),
      setQuantity: (variantId: string, quantity: number) =>
        mutate(() => api.cart.setQuantity(variantId, quantity)),
      remove: (variantId: string) => mutate(() => api.cart.remove(variantId)),
      applyCoupon: (code: string) => mutate(() => api.cart.applyCoupon(code)),
      removeCoupon: () => mutate(() => api.cart.removeCoupon()),
      clearError: () => setError(null),
    }),
    [cart, loading, error, refresh, mutate],
  )

  return <CartContext.Provider value={value}>{children}</CartContext.Provider>
}

export function useCart(): CartState {
  const cart = useContext(CartContext)

  if (!cart) {
    throw new Error('useCart must be used inside a CartProvider')
  }

  return cart
}
